import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { updateDepartment } from '@/modules/Employee/services'
import { Department } from '@/modules/Employee/types/response'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { DEPARTMENT_STATUS } from '@/modules/Employee/constants'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import KeycloakGroupSelect from '@/components/Form/Shared/KeycloakGroupSelect'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { updateDepartmentDefaultGroups } from '@/modules/Authorization/services'

export interface DepartmentEditModalProps extends ModalProps {
    department: Department
    refetch: () => void
}

interface FormValues {
    department_name: string
    department_code: string
    department_description: string
    department_status: string
    department_leader_id: number | null
    keycloak_default_group_paths: string[]
}

const DepartmentEditModal: React.FC<DepartmentEditModalProps> = ({
    open,
    onClose,
    department,
    refetch,
}) => {
    const { t } = useTranslation()
    const { isAdmin } = usePermissionsStore()

    const { control, handleSubmit } = useForm<FormValues>({
        defaultValues: {
            department_name: department.department_name || '',
            department_code: department.department_code || '',
            department_description: department.department_description || '',
            department_status: department.department_status || 'active',
            department_leader_id: department.department_leader_id || null,
            keycloak_default_group_paths:
                department.keycloak_default_group_paths ??
                (department.keycloak_default_group_path
                    ? [department.keycloak_default_group_path]
                    : []),
        },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: updateDepartment },
        {
            reactQueryKeys: QUERY_KEYS.DEPARTMENTS,
            successMessage: 'department_updated_success',
            errorMessage: 'error_occurred',
        }
    )
    const {
        mutateAsync: updateDefaultGroup,
        isPending: isUpdatingDefaultGroup,
    } = useMutationWithHandler(
        {
            mutationFn: ({
                departmentId,
                groupPaths,
            }: {
                departmentId: number
                groupPaths: string[]
            }) => updateDepartmentDefaultGroups(departmentId, groupPaths),
        },
        {
            reactQueryKeys: QUERY_KEYS.DEPARTMENTS,
            successMessage: 'Đã cập nhật group mặc định',
            errorMessage: 'Không thể cập nhật group mặc định',
        }
    )

    const onSubmit = async (data: FormValues) => {
        const updatedDepartment = await mutateAsync({
            department_id: department.department_id,
            department_name: data.department_name,
            department_code: data.department_code,
            department_description: data.department_description,
            department_status: data.department_status,
            department_leader_id: data.department_leader_id ?? undefined,
        })
        if (!updatedDepartment) return

        if (isAdmin) {
            const updatedDefaultGroups = await updateDefaultGroup({
                departmentId: department.department_id,
                groupPaths: data.keycloak_default_group_paths,
            })
            if (!updatedDefaultGroups) {
                refetch()
                return
            }
        }
        refetch()
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('edit_department')}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="p-4 space-y-4">
                    <FormItem
                        control={control}
                        name="department_name"
                        type="textfield"
                        label={t('department_name')}
                        placeholder={t('enter_department_name')}
                    />

                    <FormItem
                        control={control}
                        name="department_code"
                        type="textfield"
                        label={t('department_code')}
                        placeholder={t('enter_department_code')}
                    />

                    <EmployeeSelect
                        control={control}
                        name="department_leader_id"
                        label="department_leader"
                        onlyActive
                    />

                    {isAdmin && (
                        <KeycloakGroupSelect
                            control={control}
                            name="keycloak_default_group_paths"
                            label="Group mặc định theo phòng ban"
                        />
                    )}

                    <FormItem
                        control={control}
                        name="department_description"
                        type="textarea"
                        label={t('department_description')}
                        placeholder={t('enter_department_description')}
                    />

                    <FormItem
                        control={control}
                        name="department_status"
                        type="select"
                        label={t('status')}
                        options={DEPARTMENT_STATUS(t)}
                    />

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            type="button"
                        >
                            {t('cancel')}
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            loading={isPending || isUpdatingDefaultGroup}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </div>
            </form>
        </Dialog>
    )
}

export default DepartmentEditModal
